<script setup lang="ts">
import { Head, Link } from '@inertiajs/vue3'
import { ArrowLeft, Mail, Phone } from 'lucide-vue-next'

import StatusBadge from '@/components/applications/StatusBadge.vue'
import StatusTimeline from '@/components/applications/StatusTimeline.vue'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import PortalLayout from '@/layouts/PortalLayout.vue'
import { formatAmount, formatLabel } from '@/lib/utils'
import { portalOrganizationApplicationsPath } from '@/routes'

interface Application {
  id: string
  status: string
  loan_type: string
  purpose: string
  amount: number
  loan_term: number | null
  submission_channel: string | null
  customer_name: string
  customer_phone: string
  customer_email: string
  partner_name: string
  taken_at: string | null
  accepted_at: string | null
  approved_at: string | null
  settled_at: string | null
  rejected_at: string | null
  rejection_reason: string | null
  rejected_on: string | null
  created_at: string
}

defineProps<{ application: Application }>()
</script>

<template>
  <Head title="Application" />
  <PortalLayout actor-type="organization">
    <div class="space-y-6">
      <div class="flex items-center gap-3">
        <Link :href="portalOrganizationApplicationsPath()" class="text-muted-foreground hover:text-foreground">
          <ArrowLeft class="size-4" />
        </Link>
        <div>
          <h1 class="text-xl font-semibold">{{ application.customer_name }}</h1>
          <p class="text-muted-foreground text-sm">
            {{ formatLabel(application.purpose) }} · {{ formatAmount(application.amount) }}
          </p>
        </div>
        <StatusBadge :status="application.status" />
      </div>

      <!-- Timeline -->
      <Card>
        <CardHeader><CardTitle class="text-base">Status Progress</CardTitle></CardHeader>
        <CardContent>
          <StatusTimeline
            :status="application.status"
            :created-at="application.created_at"
            :taken-at="application.taken_at"
            :accepted-at="application.accepted_at"
            :approved-at="application.approved_at"
            :settled-at="application.settled_at"
            :rejected-at="application.rejected_at"
            :rejection-reason="application.rejection_reason"
            :rejected-on="application.rejected_on"
          />
        </CardContent>
      </Card>

      <!-- Application details -->
      <Card>
        <CardHeader><CardTitle class="text-base">Application Details</CardTitle></CardHeader>
        <CardContent>
          <dl class="grid grid-cols-1 gap-y-4 text-sm sm:grid-cols-2 lg:grid-cols-3">
            <div>
              <dt class="text-muted-foreground">Customer</dt>
              <dd class="mt-1 font-medium">{{ application.customer_name }}</dd>
            </div>
            <div>
              <dt class="text-muted-foreground">Phone</dt>
              <dd class="mt-1">
                <a
                  :href="`tel:${application.customer_phone}`"
                  class="flex items-center gap-1.5 font-medium hover:underline"
                >
                  <Phone class="size-3.5 shrink-0" />{{ application.customer_phone }}
                </a>
              </dd>
            </div>
            <div>
              <dt class="text-muted-foreground">Email</dt>
              <dd class="mt-1">
                <a
                  :href="`mailto:${application.customer_email}`"
                  class="flex items-center gap-1.5 font-medium hover:underline"
                >
                  <Mail class="size-3.5 shrink-0" />
                  <span class="truncate">{{ application.customer_email }}</span>
                </a>
              </dd>
            </div>
            <div>
              <dt class="text-muted-foreground">Partner</dt>
              <dd class="mt-1 font-medium">{{ application.partner_name }}</dd>
            </div>
            <div>
              <dt class="text-muted-foreground">Type</dt>
              <dd class="mt-1 font-medium">{{ formatLabel(application.loan_type) }}</dd>
            </div>
            <div>
              <dt class="text-muted-foreground">Purpose</dt>
              <dd class="mt-1 font-medium">{{ formatLabel(application.purpose) }}</dd>
            </div>
            <div>
              <dt class="text-muted-foreground">Amount</dt>
              <dd class="mt-1 font-medium">{{ formatAmount(application.amount) }}</dd>
            </div>
            <div v-if="application.loan_term">
              <dt class="text-muted-foreground">Term</dt>
              <dd class="mt-1 font-medium">{{ application.loan_term }} months</dd>
            </div>
            <div v-if="application.submission_channel">
              <dt class="text-muted-foreground">Channel</dt>
              <dd class="mt-1 font-medium">{{ formatLabel(application.submission_channel) }}</dd>
            </div>
          </dl>
        </CardContent>
      </Card>
    </div>
  </PortalLayout>
</template>
